import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { getAuthToken, setAuthToken, clearAuthToken } from './tokenStorage';
import { getApkBuild } from './version';

export { getAuthToken, setAuthToken, clearAuthToken };

const envBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim();
const normalizedBackendUrl = envBackendUrl.replace(/\/+$/, '');

export const API_BASE = normalizedBackendUrl ? `${normalizedBackendUrl}/api` : '/api';

// Track whether an upload is in progress so we don't redirect mid-upload
let _uploadInProgress = false;
let _pendingLogout = false;

export function setUploadInProgress(v) { _uploadInProgress = !!v; }
export function hasPendingLogout() { return _pendingLogout; }
export function clearPendingLogout() { _pendingLogout = false; }

// Send app version + auth token with every request
axios.interceptors.request.use(async (config) => {
  const ver = localStorage.getItem('app_version');
  if (ver) config.headers['X-App-Version'] = ver;

  const token = await getAuthToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;

  return config;
});

// Backend-triggered force update: if backend says our build is outdated, nuke and reload.
let _updateTriggered = false;
axios.interceptors.response.use(
  async response => {
    if (!_updateTriggered) {
      const minBuild = response.headers?.['x-min-build'];
      if (minBuild) {
        const localBuild = Number(localStorage.getItem('app_build_time') || 0);
        if (localBuild > 0 && localBuild < Number(minBuild)) {
          _updateTriggered = true;
          console.log(`[update] Backend requires build ${minBuild}, we have ${localBuild} -- force updating`);
          localStorage.setItem('app_build_time', minBuild);
          if (navigator.serviceWorker) {
            navigator.serviceWorker.getRegistration().then(reg => {
              if (reg) reg.unregister();
              if (window.caches) caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))));
            }).finally(() => window.location.reload());
          } else {
            window.location.reload();
          }
        }
      }
    }

    // Native APK force-update check (separate from web bundle X-Min-Build)
    const minApkBuildHeader = response.headers['x-min-apk-build'];
    const latestApkBuildHeader = response.headers['x-latest-apk-build'];
    if (Capacitor.isNativePlatform() && minApkBuildHeader) {
      const minApkBuild = parseInt(minApkBuildHeader, 10) || 0;
      const latestApkBuild = parseInt(latestApkBuildHeader, 10) || 0;
      if (minApkBuild > 0) {
        try {
          const currentApkBuild = await getApkBuild();
          if (currentApkBuild > 0 && currentApkBuild < minApkBuild) {
            // Dispatch a global event the modal listens to (don't block the response)
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('force-apk-update', {
                detail: { current: currentApkBuild, latest: latestApkBuild },
              }));
            }
          }
        } catch (e) {
          // Don't let an APK build check failure break normal API responses
          console.warn('APK build check failed:', e);
        }
      }
    }

    return response;
  },
  async error => {
    // On 401, redirect to login
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/')) {
      if (_uploadInProgress) {
        _pendingLogout = true;
        return Promise.reject(error);
      }
      await clearAuthToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);
